import React, { createContext, useContext, useState, useEffect } from 'react';
import { getHeaders } from '../utils/csrf';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      // Clear corrupted data
      localStorage.removeItem('user');
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Check authentication status on mount and when user changes
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const headers = await getHeaders();
        const response = await fetch('/api/v1/auth/check', {
          headers,
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.user) {
            setUser(data.user);
            localStorage.setItem('user', JSON.stringify(data.user));
          } else {
            setUser(null);
            localStorage.removeItem('user');
          }
        } else {
          setUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setUser(null);
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const register = async (name, email, password) => {
    try {
      const headers = await getHeaders();
      const response = await fetch('/users', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user: {
            name,
            email,
            password,
            password_confirmation: password
          }
        }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.user) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
          return { success: true };
        } else {
          return { success: false, error: 'Invalid response from server' };
        }
      } else {
        const error = await response.json().catch(() => ({ message: 'Registration failed' }));
        return { success: false, error: error.message || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: 'Registration failed' };
    }
  };

  const login = async (userData) => {
    try {
      if (userData && userData.user) {
        setUser(userData.user);
        localStorage.setItem('user', JSON.stringify(userData.user));
        return { success: true };
      } else {
        return { success: false, error: 'Invalid user data' };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: 'Login failed' };
    }
  };

  const loginWithFacebook = async () => {
    try {
      // First, redirect to Facebook OAuth
      window.location.href = '/users/auth/facebook';
      return { success: true };
    } catch (error) {
      console.error('Facebook login failed:', error);
      return { success: false, error: 'Facebook login failed' };
    }
  };

  const logout = async () => {
    try {
      const headers = await getHeaders();
      const response = await fetch('/users/sign_out', {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });

      // Even if the server returns an error (like 401), we still want to clear the local state
      if (!response.ok) {
        console.warn('Server logout failed, but clearing local state anyway');
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      // Always clear the local state regardless of server response
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: !!(user && user.is_admin),
    login,
    logout,
    register,
    loginWithFacebook
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 